// pages/superadmin/AiGovernanceSettings.jsx
//
// Phase 5, AI Governance Layer superadmin screen. Each row is one
// AiGovernanceSetting (one per task_type, daily_briefing, outreach_email,
// etc). aiGateway.resolveGovernance() reads these before every AI call, so
// editing a row here changes which provider/model handles that task on
// the very next request, no deploy needed. Previously these rows could
// only be changed via seedAiGovernanceSettings.js or a raw DB query; this
// is the UI that comment always claimed existed.
//
// Styling matches AiPerformanceDashboard.jsx / SalesWorkspace.jsx exactly
// (same CSS custom properties, same header/card/table patterns) rather
// than inventing a new visual language for one more internal screen.
import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import apiService from "../../../services/api";
import NotificationBell from "../../../components/NotificationBell";
import { useAuth } from "../../../contexts/AuthContext";
import { Switch } from "../../../arc/components/switch/switch";
import { TagInput } from "../../../arc/components/tag-input/tag-input";
import { Select } from "../../../arc/components/select/select";
import { MoneyInput } from "../../../arc/components/money-input/money-input";
import { Input } from "../../../arc/components/input/input";
import { Skeleton } from "../../../arc/components/skeleton/skeleton";
import InfoTooltip from "../../../components/InfoTooltip";

const styles = `
  /* Cal.com-style system: white canvas, hairline cards, near-black ink,
   * sentence-case Inter labels, status shown as a dot not a tint. Mirrors
   * SalesWorkspace.jsx. */
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }



  /* dark: flat cards need a hairline to separate from the #101010 canvas */
  .dash-root.dark .metric-card.is-hero { border-color: var(--border); }

  html, body { overflow-x: hidden; }
  .dash-root { min-height: 100vh; background: var(--bg); color: var(--text); font-family: var(--sans); font-size: 14px; line-height: 1.5; -webkit-font-smoothing: antialiased; overflow-x: clip; }

  /* HEADER, flat AXO navy, same as SalesWorkspace */
  .dash-header { background: var(--header-bg); border-bottom: 1px solid var(--header-border); position: sticky; top: 0; z-index: 100; }
  .dash-header-inner { max-width: 1000px; margin: 0 auto; padding: 0 28px; min-height: 64px; display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .dash-brand { display: flex; align-items: center; gap: 12px; padding: 12px 0; }
  .btn-back { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: var(--radius-sm); color: var(--header-text); text-decoration: none; }
  .btn-back:hover { background: var(--header-btn-hover-bg); color: #fff; }
  .dash-brand-mark { display: inline-flex; align-items: center; justify-content: center; width: 32px; height: 32px; flex-shrink: 0; border-radius: var(--radius-sm); background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); color: #fff; }
  .dash-brand-text { display: flex; flex-direction: column; gap: 1px; }
  .dash-brand-name { font-size: 14px; font-weight: 600; color: #fff; letter-spacing: -0.01em; }
  .dash-brand-sub { font-size: 12px; color: var(--header-text-muted); }
  .dash-header-actions { display: flex; align-items: center; gap: 8px; padding: 12px 0; }
  .btn-header { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: 36px; padding: 0 14px; background: var(--header-btn-bg); border: 1px solid var(--header-btn-border); border-radius: 8px; font-family: var(--sans); font-size: 12.5px; font-weight: 500; color: var(--header-text); cursor: pointer; white-space: nowrap; text-decoration: none; transition: background var(--transition), color var(--transition), border-color var(--transition); }
  .btn-header:hover:not(:disabled) { background: var(--header-btn-hover-bg); color: #fff; }
  .btn-header:disabled { opacity: 0.5; cursor: not-allowed; }
  .btn-header:focus-visible { outline: 2px solid rgba(255,255,255,0.6); outline-offset: 2px; }
  .btn-header.is-accent { background: rgba(255,255,255,0.12); }
  .btn-header svg { flex-shrink: 0; }
  .btn-header.is-refreshing svg { animation: refresh-spin 0.6s linear; }
  @keyframes refresh-spin { to { transform: rotate(360deg); } }

  .dash-main { max-width: 1000px; margin: 0 auto; padding: 32px 28px 56px; display: flex; flex-direction: column; gap: 24px; min-width: 0; }
  .dash-main > div { animation: card-in 0.25s ease both; }
  @keyframes card-in { from { opacity: 0; } to { opacity: 1; } }

  /* SECTIONS, plain sentence-case heading, no rule, no mono */
  .section-head { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
  .section-title { display: inline-flex; align-items: center; gap: 4px; font-size: 16px; font-weight: 600; letter-spacing: -0.01em; color: var(--text); }
  .section-caption { font-size: 13px; color: var(--text-muted); }

  /* METRIC CARDS, white + hairline; grey fill marks the headline figure */
  .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; }
  .metric-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px; min-width: 0; }
  .metric-card.is-hero { background: var(--surface-sunken); border-color: transparent; }
  .metric-label { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 500; color: var(--text-muted); margin-bottom: 8px; }
  .metric-value { font-size: 28px; font-weight: 600; line-height: 1.15; color: var(--text); letter-spacing: -0.03em; font-variant-numeric: tabular-nums; overflow-wrap: anywhere; }
  .metric-value.muted { color: var(--text-faint); font-size: 15px; font-weight: 500; letter-spacing: 0; }
  .metric-note { font-size: 13px; color: var(--text-muted); margin-top: 6px; }

  /* BADGE, neutral pill, a small dot carries the status */
  .badge { display: inline-flex; align-items: center; gap: 6px; font-family: var(--sans); font-size: 12px; font-weight: 500; letter-spacing: 0; text-transform: none; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); border: none; color: var(--text); white-space: nowrap; }
  .badge::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .badge.confidence-moderate, .badge.direction-growing { --dot: var(--green); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-low { --dot: var(--amber); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.direction-declining { --dot: var(--red); color: var(--text); background: var(--surface-sunken); border: none; }
  .badge.confidence-insufficient_data, .badge.direction-flat { --dot: var(--text-faint); color: var(--text-muted); background: var(--surface-sunken); border: none; }

  /* CHART, one ink colour, no gradient */
  .chart-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 24px; }
  .chart-bars { display: flex; align-items: flex-end; gap: 12px; height: 150px; padding-top: 8px; }
  .chart-bar-col { flex: 1 1 0; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; height: 100%; min-width: 0; gap: 8px; }
  .chart-bar-track { width: 100%; flex: 1 1 auto; display: flex; align-items: flex-end; }
  .chart-bar-fill { width: 100%; border-radius: 4px 4px 0 0; background: var(--ink); min-height: 3px; transform-origin: bottom; animation: bar-grow-y 0.4s ease-out both; }
  @keyframes bar-grow-y { from { transform: scaleY(0); } to { transform: scaleY(1); } }
  .chart-bar-fill.is-empty { background: var(--border); }
  .chart-bar-value { font-size: 12px; font-weight: 500; color: var(--text-muted); white-space: nowrap; font-variant-numeric: tabular-nums; }
  .chart-bar-label { font-size: 12px; color: var(--text-muted); white-space: nowrap; }

  /* LIST ROWS */
  .table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .breakdown-row { display: flex; align-items: center; gap: 16px; padding: 16px 22px; border-bottom: 1px solid var(--border); }
  .breakdown-row:last-child { border-bottom: none; }
  .breakdown-main { flex: 1 1 auto; min-width: 0; }
  .breakdown-task { font-size: 14px; font-weight: 500; color: var(--text); }
  .breakdown-sub { font-size: 13px; color: var(--text-muted); margin-top: 1px; }
  .breakdown-bar-track { width: 100%; height: 4px; border-radius: 9999px; background: var(--surface-sunken); margin-top: 10px; overflow: hidden; }
  .breakdown-bar-fill { height: 100%; border-radius: 9999px; background: var(--ink); transform-origin: left; animation: bar-grow 0.4s ease-out both; }
  @keyframes bar-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
  .breakdown-cost { flex-shrink: 0; font-size: 14px; font-weight: 600; color: var(--text); font-variant-numeric: tabular-nums; }

  .notice-card { display: flex; align-items: flex-start; gap: 10px; background: var(--surface-sunken); border: none; border-radius: var(--radius); padding: 12px 16px; font-size: 14px; line-height: 1.5; color: var(--text-body); }
  .notice-card svg { flex-shrink: 0; color: var(--amber); margin-top: 3px; }

  .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 56px 20px; gap: 10px; text-align: center; }
  .empty-state-label { font-size: 14px; color: var(--text-muted); }
  .spinner { width: 24px; height: 24px; border: 2px solid var(--border); border-top-color: var(--ink); border-radius: 50%; animation: spin 0.7s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* GOVERNANCE TABLE */
  .gov-table-wrap { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; }
  .gov-row { display: flex; align-items: center; gap: 16px; padding: 18px 22px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
  .gov-row:last-child { border-bottom: none; }
  .gov-row-main { flex: 1 1 200px; min-width: 0; }
  .gov-task-name { font-size: 14px; font-weight: 600; color: var(--text); }
  .gov-task-note { font-size: 13px; color: var(--text-muted); margin-top: 1px; }

  .gov-field { display: flex; flex-direction: column; gap: 4px; }
  .gov-money { --money-input-height: 40px; --text-3xl: 16px; --text-2xl: 15px; --radius-panel: var(--radius); width: 150px; }
  .gov-field label { font-size: 13px; font-weight: 500; color: var(--text-muted); }
  .gov-select, .gov-input { height: 36px; font-family: var(--sans); font-size: 14px; padding: 0 14px; border: 1px solid var(--border); border-radius: var(--radius); background: var(--surface); color: var(--text); min-width: 0; outline: none; }
  .gov-select { min-width: 120px; }
  .gov-input.model { min-width: 190px; }
  .gov-input.cost { width: 88px; }
  .gov-select:focus, .gov-input:focus { border-color: var(--text); }

  .gov-toggle { display: inline-flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
  .gov-toggle input { accent-color: var(--ink); width: 16px; height: 16px; cursor: pointer; }
  .gov-toggle span { font-size: 13px; color: var(--text-body); }

  .gov-status { display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 500; padding: 3px 10px; border-radius: 9999px; background: var(--surface-sunken); color: var(--text); white-space: nowrap; }
  .gov-status::before { content: ''; width: 6px; height: 6px; border-radius: 50%; background: var(--dot, var(--text-faint)); }
  .gov-status.saved { --dot: var(--green); }
  .gov-status.saving { --dot: var(--amber); }
  .gov-status.error { --dot: var(--red); }


  /* BRIEFING SETTINGS, master switch + recipients */
  .brief-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 20px 22px; display: flex; flex-direction: column; gap: 18px; }
  .brief-top { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
  .brief-top-title { display: flex; align-items: center; gap: 4px; font-size: 14px; font-weight: 600; color: var(--text); }
  .brief-top-sub { font-size: 13px; color: var(--text-muted); margin-top: 1px; }
  .brief-divider { border: none; border-top: 1px solid var(--border); }
  .brief-group-label { font-size: 13px; font-weight: 500; color: var(--text-muted); margin-bottom: 8px; }
  .brief-radio { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; cursor: pointer; }
  .brief-radio input { accent-color: var(--ink); width: 16px; height: 16px; margin-top: 2px; cursor: pointer; }
  .brief-radio-title { font-size: 14px; font-weight: 500; color: var(--text); }
  .brief-radio-note { font-size: 13px; color: var(--text-muted); }
  .brief-actions { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
  .btn-save { height: 36px; padding: 0 16px; font-family: var(--sans); font-size: 14px; font-weight: 500; color: var(--on-ink); background: var(--ink); border: none; border-radius: var(--radius); cursor: pointer; }
  .btn-save:hover:not(:disabled) { background: var(--ink-active); }
  .btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
  .brief-error { font-size: 13px; color: var(--red); }

  @media (max-width: 640px) {
    .dash-header-inner { padding: 12px 16px; }
    .dash-brand, .dash-header-actions { padding: 0; }
    .dash-main { padding: 24px 16px 40px; }
    .metric-grid { grid-template-columns: repeat(auto-fit, minmax(0, 1fr)); }
    .btn-header span.btn-label { display: none; }
    .chart-bars { height: 110px; }
    .breakdown-row { padding: 14px 16px; }
    .gov-row { padding: 16px; }
  }
`;

const TASK_TYPE_LABELS = {
  daily_briefing: "Daily Briefing",
  opportunity_prioritisation: "Opportunity Prioritisation",
  outreach_email: "Outreach, Email",
  outreach_linkedin: "Outreach, LinkedIn",
  outreach_cold_call_intro: "Outreach, Cold Call Intro",
  outreach_follow_up: "Outreach, Follow-up",
  meeting_prep_notes: "Meeting Prep Notes",
};

const PROVIDER_MODEL_HINTS = {
  anthropic: "e.g. claude-sonnet-4-6, claude-haiku-4-5",
  openai: "e.g. gpt-5, gpt-5-mini",
  gemini: "e.g. gemini-2.5-pro, gemini-2.5-flash",
  open_source: "model name your inference server expects",
};

const BRIEF_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const RECIPIENT_OPTIONS = [
  {
    value: "ops_inbox",
    title: "Ops inbox",
    note: "Every briefing goes to the address set in OPS_ALERT_EMAIL.",
  },
  {
    value: "briefed_user",
    title: "Each person's own email",
    note: "Every briefed user gets their own briefing at their account email.",
  },
  {
    value: "custom",
    title: "Specific email addresses",
    note: "Send to the addresses below. Separate several with commas.",
  },
];

function formatDelay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default function AiGovernanceSettings({ backTo = "/super-admin" }) {
  const { logout } = useAuth();
  const [settings, setSettings] = useState([]);
  const [validProviders, setValidProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  // rowStatus[id] = 'saving' | 'saved' | 'error' | undefined
  const [rowStatus, setRowStatus] = useState({});
  const [seeding, setSeeding] = useState(false);

  // Daily briefing switch + recipients
  const [brief, setBrief] = useState(null);
  const [briefMode, setBriefMode] = useState("ops_inbox");
  const [briefEmails, setBriefEmails] = useState([]);
  const [briefEmailError, setBriefEmailError] = useState(null);
  const [briefSaving, setBriefSaving] = useState(false);
  const [briefStatus, setBriefStatus] = useState(null); // "saved" | null
  const [briefError, setBriefError] = useState(null);
  const [opsInboxConfigured, setOpsInboxConfigured] = useState(true);

  const applyBrief = (settings) => {
    setBrief(settings);
    setBriefMode(settings.recipientMode);
    setBriefEmails(settings.recipientEmails || []);
    setBriefEmailError(null);
  };

  const loadBrief = useCallback(async () => {
    try {
      const res = await apiService.getBriefingSettings();
      applyBrief(res.settings);
      setOpsInboxConfigured(res.opsInboxConfigured !== false);
    } catch (err) {
      setBriefError(
        err.response?.data?.message || "Failed to load briefing settings",
      );
    }
  }, []);

  const saveBrief = async (payload) => {
    setBriefSaving(true);
    setBriefError(null);
    try {
      const res = await apiService.updateBriefingSettings(payload);
      applyBrief(res.settings);
      setBriefStatus("saved");
      await formatDelay(1400);
      setBriefStatus(null);
    } catch (err) {
      setBriefError(
        err.response?.data?.message || "Failed to save briefing settings",
      );
    } finally {
      setBriefSaving(false);
    }
  };

  const handleBriefToggle = (enabled) => saveBrief({ enabled });

  // Same rule as the server (revenueBriefingSettingsService EMAIL_RE) so bad
  // addresses are caught before Save. Pasted lists ("a@x.com, b@y.com") split
  // into one tag each.
  const handleBriefEmailsChange = (next) => {
    const parts = next.flatMap((t) => t.split(/[\s,;]+/)).filter(Boolean);
    const valid = [];
    let bad = null;
    parts.forEach((e) => {
      if (!BRIEF_EMAIL_RE.test(e)) bad = bad || e;
      else if (!valid.some((v) => v.toLowerCase() === e.toLowerCase()))
        valid.push(e);
    });
    setBriefEmails(valid);
    setBriefEmailError(bad ? `"${bad}" isn't a valid email address.` : null);
  };

  const handleBriefRecipientsSave = () =>
    saveBrief({
      recipientMode: briefMode,
      ...(briefMode === "custom" ? { recipientEmails: briefEmails } : {}),
    });

  const briefDirty =
    brief &&
    (briefMode !== brief.recipientMode ||
      (briefMode === "custom" &&
        briefEmails.join(", ") !== (brief.recipientEmails || []).join(", ")));

  const load = useCallback(async () => {
    try {
      setError(null);
      const res = await apiService.getAiGovernanceSettings();
      setSettings(res.settings || []);
      setValidProviders(res.validProviders || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load AI governance settings",
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadBrief();
  }, [load, loadBrief]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await Promise.all([load(), loadBrief()]);
  };

  // First-run convenience: create the default row for every task type
  // without needing the CLI seeder.
  const handleSeedDefaults = async () => {
    setSeeding(true);
    setError(null);
    try {
      const res = await apiService.seedAiGovernanceDefaults();
      setSettings(res.settings || []);
      setValidProviders(res.validProviders || []);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to create default settings",
      );
    } finally {
      setSeeding(false);
    }
  };

  const handleFieldChange = (id, field, value) => {
    setSettings((prev) =>
      prev.map((s) => (s.id === id ? { ...s, [field]: value } : s)),
    );
  };

  const persist = async (id, updates) => {
    setRowStatus((prev) => ({ ...prev, [id]: "saving" }));
    try {
      const res = await apiService.updateAiGovernanceSetting(id, updates);
      setSettings((prev) => prev.map((s) => (s.id === id ? res.setting : s)));
      setRowStatus((prev) => ({ ...prev, [id]: "saved" }));
      await formatDelay(1400);
      setRowStatus((prev) => ({ ...prev, [id]: undefined }));
    } catch (err) {
      setRowStatus((prev) => ({ ...prev, [id]: "error" }));
    }
  };

  const handleProviderChange = (id, provider) => {
    handleFieldChange(id, "activeProvider", provider);
    persist(id, { activeProvider: provider });
  };

  const handleModelBlur = (id, model) => {
    persist(id, { activeModel: model });
  };

  const handleCostBlur = (id, value) => {
    const parsed = value === "" || value == null ? null : Number(value);
    persist(id, { maxCostPerRequestGbp: parsed });
  };

  const handleEnabledToggle = (id, enabled) => {
    handleFieldChange(id, "enabled", enabled);
    persist(id, { enabled });
  };

  return (
    <div className="dash-root">
      <style>{styles}</style>

      <header className="dash-header">
        <div className="dash-header-inner">
          <div className="dash-brand">
            <Link
              to={backTo}
              className="btn-back"
              title="Back to Dashboard"
              aria-label="Back to Dashboard"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12L6 8l4-4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Link>
            <div className="dash-brand-mark">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.5l1.7 3.7 4 .5-3 2.7.9 4-3.6-2-3.6 2 .9-4-3-2.7 4-.5L8 1.5z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <div className="dash-brand-text">
              <div className="dash-brand-name">AI Governance</div>
              <div className="dash-brand-sub">Provider &amp; Model Control</div>
            </div>
          </div>
          <div className="dash-header-actions">
            <NotificationBell />
            <Link
              to="/super-admin/ai-governance/deal-values"
              className="btn-header"
            >
              <span className="btn-label">Deal Values</span>
            </Link>
            <button
              type="button"
              className={`btn-header${refreshing ? " is-refreshing" : ""}`}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.5 8a5.5 5.5 0 11-1.6-3.9M13.5 2.5V6h-3.5"
                  stroke="currentColor"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span className="btn-label">Refresh</span>
            </button>
            <button type="button" className="btn-header" onClick={logout}>
              <span className="btn-label">Log out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="dash-main">
        <section>
          <div className="section-head">
            <div className="section-title">
              Daily revenue briefing
              <InfoTooltip
                label="What is the daily revenue briefing?"
                content="An automated briefing that runs each weekday morning, generating each recipient's prioritised list of opportunities and emailing it to them."
              />
            </div>
            <div className="section-caption">
              Runs automatically at 07:00 (Europe/London), Monday to Friday.
            </div>
          </div>

          {!brief ? (
            briefError ? (
              <div className="empty-state">
                <div className="empty-state-label">{briefError}</div>
              </div>
            ) : (
              <div className="empty-state" style={{ width: "100%" }}>
                <Skeleton lines={4} label="Loading brief" />
              </div>
            )
          ) : (
            <div className="brief-card">
              <div className="brief-top">
                <div>
                  <div className="brief-top-title">
                    Send the daily briefing
                    <InfoTooltip
                      label="What does send the daily briefing control?"
                      content="When on, the 07:00 run generates and emails the daily briefing. When off, no briefings are generated or sent."
                    />
                  </div>
                  <div className="brief-top-sub">
                    {brief.enabled
                      ? "On. The 07:00 run generates briefings and emails them."
                      : "Off. The 07:00 run does nothing until you turn this back on."}
                  </div>
                </div>
                <Switch
                  checked={brief.enabled}
                  disabled={briefSaving}
                  onCheckedChange={handleBriefToggle}
                  aria-label="Send the daily briefing"
                  label={brief.enabled ? "On" : "Off"}
                />
              </div>

              <hr className="brief-divider" />

              <div>
                <div className="brief-group-label">Who receives the email</div>
                {RECIPIENT_OPTIONS.map((o) => (
                  <label className="brief-radio" key={o.value}>
                    <input
                      type="radio"
                      name="briefing-recipients"
                      value={o.value}
                      checked={briefMode === o.value}
                      onChange={() => setBriefMode(o.value)}
                    />
                    <span>
                      <div className="brief-radio-title">{o.title}</div>
                      <div className="brief-radio-note">{o.note}</div>
                    </span>
                  </label>
                ))}
                {briefMode === "custom" && (
                  <div style={{ marginTop: 10 }}>
                    <TagInput
                      label="Recipient email addresses"
                      value={briefEmails}
                      onValueChange={handleBriefEmailsChange}
                      placeholder="nina@example.com"
                      description={
                        briefEmailError ||
                        "Press Enter or comma to add. Paste a list to add several at once."
                      }
                    />
                  </div>
                )}
                {briefMode === "ops_inbox" && !opsInboxConfigured && (
                  <div className="brief-error" style={{ marginTop: 6 }}>
                    OPS_ALERT_EMAIL isn't set on the server, so no email will be
                    sent in this mode.
                  </div>
                )}
              </div>

              <div className="brief-actions">
                <button
                  type="button"
                  className="btn-save"
                  onClick={handleBriefRecipientsSave}
                  disabled={briefSaving || !briefDirty}
                >
                  {briefSaving ? "Saving…" : "Save recipients"}
                </button>
                {briefStatus === "saved" && (
                  <span className="gov-status saved">Saved</span>
                )}
                {briefError && (
                  <span className="brief-error">{briefError}</span>
                )}
              </div>
            </div>
          )}
        </section>

        <section>
          <div className="section-head">
            <div className="section-title">
              Task Types
              <InfoTooltip
                label="What are task types?"
                content="One row per kind of AI task. Each sets the provider, model, per-request cost cap and whether the task is allowed. Changes take effect on the next AI call, no deploy needed."
              />
            </div>
            <div className="section-caption">
              Changes apply to the very next AI call for that task, no deploy
              needed.
            </div>
          </div>

          {loading ? (
            <div className="empty-state" style={{ width: "100%" }}>
              <Skeleton lines={5} label="Loading settings" />
            </div>
          ) : error ? (
            <div className="empty-state">
              <div className="empty-state-label">{error}</div>
            </div>
          ) : settings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-label">No AI task settings yet</div>
              <div className="empty-state-label">
                Create the default provider, model and per-request cost cap for
                every AI task. You can change any row once it&apos;s created.
              </div>
              <button
                className="btn-save"
                disabled={seeding}
                onClick={handleSeedDefaults}
              >
                {seeding ? "Creating…" : "Create default settings"}
              </button>
            </div>
          ) : (
            <div className="gov-table-wrap">
              {settings.map((s) => (
                <div className="gov-row" key={s.id}>
                  <div className="gov-row-main">
                    <div className="gov-task-name">
                      {TASK_TYPE_LABELS[s.taskType] || s.taskType}
                    </div>
                    <div className="gov-task-note">{s.taskType}</div>
                  </div>

                  <div className="gov-field">
                    <Select
                      label="Provider"
                      className="gov-select"
                      value={s.activeProvider}
                      onValueChange={(value) =>
                        handleProviderChange(s.id, value)
                      }
                      options={validProviders.map((p) => ({
                        value: p,
                        label: p,
                      }))}
                    />
                  </div>

                  <div className="gov-field">
                    <label htmlFor={`model-${s.id}`}>Model</label>
                    <Input
                      label="Model"
                      hideLabel
                      id={`model-${s.id}`}
                      className="gov-input model"
                      type="text"
                      value={s.activeModel}
                      placeholder={PROVIDER_MODEL_HINTS[s.activeProvider] || ""}
                      onChange={(e) =>
                        handleFieldChange(s.id, "activeModel", e.target.value)
                      }
                      onBlur={(e) => handleModelBlur(s.id, e.target.value)}
                    />
                  </div>

                  <div className="gov-field">
                    <label>Max £/req</label>
                    <MoneyInput
                      id={`cost-${s.id}`}
                      label="Max £/req"
                      hideLabel
                      className="gov-money"
                      currency="GBP"
                      currencies={["GBP"]}
                      locale="en-GB"
                      quickAdd={[]}
                      value={
                        s.maxCostPerRequestGbp == null
                          ? null
                          : Math.round(s.maxCostPerRequestGbp * 100)
                      }
                      onValueChange={(minor, details) =>
                        handleFieldChange(
                          s.id,
                          "maxCostPerRequestGbp",
                          details.major,
                        )
                      }
                      onBlur={() =>
                        handleCostBlur(s.id, s.maxCostPerRequestGbp)
                      }
                    />
                  </div>

                  <label className="gov-toggle">
                    <input
                      type="checkbox"
                      checked={s.enabled}
                      onChange={(e) =>
                        handleEnabledToggle(s.id, e.target.checked)
                      }
                    />
                    <span>Enabled</span>
                  </label>

                  {rowStatus[s.id] && (
                    <span className={`gov-status ${rowStatus[s.id]}`}>
                      {rowStatus[s.id] === "saving"
                        ? "Saving…"
                        : rowStatus[s.id] === "saved"
                          ? "Saved"
                          : "Failed"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
